import Catalogue from './pages/Catalogue';
import Records, { LegacyReview, LegacyCustomerRedirect } from './pages/Records';
import CustomFieldsSettings from './pages/settings/CustomFieldsSettings';
import AcceptInvite from './pages/AcceptInvite';
import TeamSettings from './pages/settings/TeamSettings';
import Reports from './pages/Reports';
import Inbox from './pages/Inbox';
import EmailSettings from './pages/settings/EmailSettings';
import WhatsAppSettings from './pages/settings/WhatsAppSettings';
import WhatsAppPricing from './pages/settings/WhatsAppPricing';
import WhatsAppBusinessKnowledge from './pages/settings/WhatsAppBusinessKnowledge';
import WhatsAppPolicies from './pages/settings/WhatsAppPolicies';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './index.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import TodayDashboard from './pages/TodayDashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import NewLead from './pages/NewLead';
import Settings from './pages/Settings';
import AccountSettings from './pages/settings/AccountSettings';
import WorkspaceSettings from './pages/settings/WorkspaceSettings';
import BillingSettings from './pages/settings/BillingSettings';
import NotificationsSettings from './pages/settings/NotificationsSettings';
import RegionalSettings from './pages/settings/RegionalSettings';
import AboutSettings from './pages/settings/AboutSettings';
import Layout from './components/Layout';
import More from './pages/More';

const RouteLoading = () => (
  <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
    <span className="sr-only">Loading page</span>
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" aria-hidden="true"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <RouteLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const location = useLocation();
  const invite = new URLSearchParams(location.search).get("invite");
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <RouteLoading />;
  }

  if (isAuthenticated) {
    return <Navigate to={invite ? `/invite?token=${encodeURIComponent(invite)}` : "/"} replace />;
  }

  return children;
};

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/invite" element={<AcceptInvite />} />
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<TodayDashboard />} />
        <Route path="more" element={<More />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="inbox/:conversationId" element={<Inbox />} />
        <Route path="inbox/email" element={<Inbox />} />
        <Route path="inbox/email/:conversationId" element={<Inbox />} />
        <Route path="settings/email" element={<EmailSettings />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/pipeline" element={<Navigate to="/leads" replace />} />
        <Route path="leads/archived" element={<Navigate to="/leads" replace />} />
        <Route path="catalogue" element={<Catalogue />} />
        <Route path="contacts" element={<Records kind="contacts" />} />
        <Route path="contacts/legacy-review" element={<LegacyReview />} />
        <Route path="contacts/:id" element={<Records kind="contacts" />} />
        <Route path="organisations" element={<Records kind="organisations" />} />
        <Route path="organisations/:id" element={<Records kind="organisations" />} />
        <Route path="customers" element={<LegacyCustomerRedirect />} />
        <Route path="customers/:id" element={<LegacyCustomerRedirect />} />
        <Route path="settings/custom-fields" element={<CustomFieldsSettings />} />
        <Route path="leads/new" element={<NewLead />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="tasks" element={<Navigate to="/" replace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="automations" element={<Navigate to="/" replace />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/whatsapp" element={<WhatsAppSettings />} />
        <Route path="settings/business-knowledge" element={<WhatsAppBusinessKnowledge />} />
        <Route path="settings/whatsapp/business-knowledge" element={<Navigate to="/settings/business-knowledge" replace />} />
        <Route path="settings/business-knowledge/pricing" element={<WhatsAppPricing />} />
        <Route path="settings/whatsapp/pricing" element={<Navigate to="/settings/business-knowledge/pricing" replace />} />
        <Route path="settings/business-knowledge/policies" element={<WhatsAppPolicies />} />
        <Route path="settings/whatsapp/policies" element={<Navigate to="/settings/business-knowledge/policies" replace />} />
        <Route path="settings/account" element={<AccountSettings />} />
        <Route path="settings/workspace" element={<WorkspaceSettings />} />
        <Route path="settings/billing" element={<BillingSettings />} />
        <Route path="settings/team" element={<TeamSettings />} />
        <Route path="settings/notifications" element={<NotificationsSettings />} />
        <Route path="settings/regional" element={<RegionalSettings />} />
        <Route path="settings/integrations" element={<Navigate to="/settings" replace />} />
        <Route path="settings/about" element={<AboutSettings />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
